import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Newspaper } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BlogCard } from "@/components/Blog/BlogCard";
import { getAllPosts, mergePosts, BlogPost } from "@/data/blogData";
import { fetchGeneratedPosts } from "@/services/blogService";
import { getBlogHomeUrl, getBlogPostUrl } from "@/lib/blogUrls";
import { useSiteLanguage } from "@/lib/useSiteLanguage";
import { blogPreviewCopy } from "@/lib/siteCopy";
import { localizeFeaturedPosts } from "@/lib/localizeBlog";

function pickPreviewPosts(posts: BlogPost[], count: number): BlogPost[] {
  const featured = posts.filter((p) => p.featured);
  if (featured.length >= count) return featured.slice(0, count);
  return posts.slice(0, count);
}

export function BlogPreview() {
  const lang = useSiteLanguage();
  const t = blogPreviewCopy[lang];
  const featuredPosts = localizeFeaturedPosts(3, lang);
  const [previewPosts, setPreviewPosts] = useState<BlogPost[]>(() =>
    pickPreviewPosts(getAllPosts(), 3),
  );

  useEffect(() => {
    let cancelled = false;
    void fetchGeneratedPosts().then((generated) => {
      if (cancelled) return;
      const merged = mergePosts(getAllPosts(), generated);
      setPreviewPosts(pickPreviewPosts(merged, 3));
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleViewAllClick = () => {
    window.location.href = getBlogHomeUrl();
  };

  const handlePostClick = (slug: string) => {
    window.location.href = getBlogPostUrl(slug);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2,
      },
    },
  };

  const headerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut" as const,
      },
    },
  };

  return (
    <section
      id="blog"
      className="py-24 md:py-32 bg-background relative overflow-hidden"
    >
      {/* Subtle Background Pattern */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-gradient-to-bl from-muted/50 to-transparent" />
        <div className="absolute bottom-0 left-0 w-1/4 h-1/4 bg-gradient-to-tr from-muted/30 to-transparent" />
      </div>

      <div className="container mx-auto px-6 lg:px-12 relative z-10">
        {/* Section Header */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-16"
        >
          <motion.div variants={headerVariants} className="max-w-2xl">
            {/* Icon Badge */}
            <div className="inline-flex items-center justify-center w-12 h-12 mb-6 border border-border rounded-lg bg-background/50">
              <Newspaper className="w-6 h-6" />
            </div>

            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-4">
              {t.title}
            </h2>
            <p className="text-lg text-muted-foreground">{t.subtitle}</p>
            <p className="text-lg text-muted-foreground">
              Daily tech insights plus evergreen guides from our engineering team.
            </p>
          </motion.div>

          <motion.div variants={headerVariants}>
            <Button
              variant="outline"
              size="lg"
              onClick={handleViewAllClick}
              className="group whitespace-nowrap"
            >
              {t.cta}
              <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Button>
          </motion.div>
        </motion.div>

        {/* Blog Cards Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8"
        >
          {previewPosts.map((post, index) => (
            <BlogCard
              key={post.id}
              post={post}
              index={index}
              variant="featured"
              onClick={() => handlePostClick(post.slug)}
            />
          ))}
        </motion.div>

        {/* Mobile View All Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5, duration: 0.5 }}
          className="mt-10 text-center md:hidden"
        >
          <Button
            variant="default"
            size="lg"
            onClick={handleViewAllClick}
            className="group w-full sm:w-auto"
          >
            {t.ctaMobile}
            <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Button>
        </motion.div>
      </div>
    </section>
  );
}
